import { useState } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import LicenseFeatureGroupCapabilitiesSection from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupCapabilitiesSection"
import LicenseFeatureGroupComposeFooter from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupComposeFooter"
import LicenseFeatureGroupContentSection from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupContentSection"
import LicenseFeatureGroupIndividualSection from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupIndividualSection"
import LicenseFeatureGroupViewSwitch from "@/v2-backoffice-client/license/featureGroup/compose/LicenseFeatureGroupViewSwitch"
import type { ComposeView } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.constants"
import {
  useFeatureGroupDraft,
  type UseFeatureGroupDraftInput,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.draft.hooks"
import {
  isPersonLens,
  useFeatureGroupLens,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.context"

/**
 * How the editor sits in its host. "fill": it takes the host's height and
 * scrolls the view inside itself, footer pinned (the package and add-on
 * panels, where the editor IS the tab). "flow": it is as tall as its
 * content and the host scrolls everything as one page, the footer sticking
 * to the bottom edge of that page (the customer's Feature access tab, where
 * a headline sits above the editor and should scroll with it).
 */
export type ComposeLayout = "fill" | "flow"

export type LicenseFeatureGroupCompositionProps = UseFeatureGroupDraftInput & {
  layout?: ComposeLayout
}

// The package editor: one draft (useFeatureGroupDraft), three views of it
// — what the app can do, what the map offers, the single picks — and the
// footer that writes or drops it. The views own their own catalog reads;
// this component only decides which one is on screen, and how the whole
// sits in its host (ComposeLayout). Through the person lens nothing
// writes, so there is no footer: the views read as one person's answers.
function LicenseFeatureGroupComposition(
  props: LicenseFeatureGroupCompositionProps,
) {
  const { featureGroup, pickedFeatures, rules, layout = "fill" } = props
  const draft = useFeatureGroupDraft(props)
  const [view, setView] = useState<ComposeView>("capabilities")
  const fills = layout === "fill"
  const isReadOnly = isPersonLens(useFeatureGroupLens())

  return (
    <div className={cn("flex flex-col gap-3", fills && "h-full min-h-0")}>
      <LicenseFeatureGroupViewSwitch
        view={view}
        pickedFeatures={draft.pickedFeatures}
        rules={draft.ruleDraft}
        onViewChange={setView}
      />
      {/* Filling, the catalog views scroll as one page here and the registry
          browser scrolls inside its own panes, so its view hands the height
          down. Flowing, the host scrolls; the browser gets a height of its
          own, since its panes cannot grow with their content. */}
      <div
        className={cn(
          "flex flex-col gap-5 pr-1",
          fills && "min-h-0 flex-1",
          fills &&
            (view === "individual" ? "overflow-hidden" : "overflow-auto"),
          !fills && view === "individual" && "h-[70vh]",
        )}
      >
        {view === "individual" ? (
          <LicenseFeatureGroupIndividualSection
            className="min-h-0 flex-1"
            pickedFeatures={draft.pickedFeatures}
            persistedFeatures={draft.persistedFeatures}
            featureGroupName={featureGroup.Name}
            isPicked={draft.isPicked}
            wasPicked={draft.wasPicked}
            onSetPicked={draft.setPicked}
          />
        ) : view === "content" ? (
          <LicenseFeatureGroupContentSection
            featureGroupName={featureGroup.Name}
            rules={draft.ruleDraft}
            persistedRules={rules}
            isPicked={draft.isPicked}
            wasPicked={draft.wasPicked}
            onRulesChange={draft.setRuleDraft}
            onSetPicked={draft.setPicked}
          />
        ) : (
          <LicenseFeatureGroupCapabilitiesSection
            featureGroupName={featureGroup.Name}
            rules={draft.ruleDraft}
            persistedRules={rules}
            isPicked={draft.isPicked}
            wasPicked={draft.wasPicked}
            onRulesChange={draft.setRuleDraft}
            onSetPicked={(capabilities, isGranted) => {
              draft.setPicked(
                capabilities.map((capability) => ({
                  Code: capability.Code,
                  Name: capability.Name,
                  ModuleName: capability.ModuleName,
                  IsSurface: true,
                })),
                isGranted,
              )
            }}
          />
        )}
      </div>
      {!isReadOnly && (
        <LicenseFeatureGroupComposeFooter
          className={cn(!fills && "sticky bottom-0 z-10 bg-background pb-1")}
          changes={draft.changes}
          isSaving={draft.isSaving}
          onReset={draft.reset}
          onSave={() => {
            void draft.save()
          }}
        />
      )}
    </div>
  )
}

export default LicenseFeatureGroupComposition
